import React, { useState } from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import SettingsModal from '../../../clientSettings/SettingsModal';
import Toggle from '../../../Toggle';
import { toast } from '../../../Toast';
import { SelectBox } from '../../shared';
import { FileBox } from './FileBox';
import { GovDoc, GovSection, PickedFile, longDate, parseDate, shortDate, sizeText, todayIso } from './governanceData';

interface Props {
    doc: GovDoc;
    sections: GovSection[];
    onClose: () => void;
    onSave: (doc: GovDoc) => void;
    onDelete: () => void;
}

// Corporate governance › Edit document: rename, move section, updated date, show / hide, replace the file (spec 708:4906)
const EditDocumentModal: React.FC<Props> = ({ doc, sections, onClose, onSave, onDelete }) => {
    const [name, setName] = useState(doc.name);
    const [section, setSection] = useState(doc.section);
    const [date, setDate] = useState(longDate(doc.updated));
    const [visible, setVisible] = useState(doc.visible);
    // a replacement chosen here; the current file until then
    const [replacement, setReplacement] = useState<PickedFile | null>(null);

    const current = sections.find(s => s.id === section);
    const save = () => {
        const updated = parseDate(date);
        if (!updated) {
            toast.error('Type the updated date like “30 June 2026”');
            return;
        }
        onSave({
            ...doc, name: name.trim(), section, visible,
            updated: replacement && updated === doc.updated ? todayIso() : updated,
            ...(replacement ? { file: replacement.file, kind: replacement.kind, sizeKb: replacement.sizeKb, uploadedBy: 'you' } : {}),
        });
    };

    return <SettingsModal icon={<FiEdit2 />} title="Edit Document" sub={`${doc.name} · ${doc.kind} · ${sizeText(doc.sizeKb)}`} onClose={onClose}
        footer={<>
            <button type="button" className="hub-delete-doc" onClick={onDelete}><FiTrash2 /> Delete Document</button>
            <button type="button" className="btn-primary" disabled={!name.trim()} onClick={save}>Save Changes</button>
        </>}>
        {replacement
            ? <FileBox file={replacement.file} sub={`${replacement.kind} · ${sizeText(replacement.sizeKb)} · replaces the current version, same link on the website`}
                removeLabel="Keep the current version" onRemove={() => setReplacement(null)} onReplace={setReplacement} />
            : <FileBox file={doc.file} sub={`Current version · uploaded ${shortDate(doc.updated)} by ${doc.uploadedBy}`}
                removeLabel="Delete the document" onRemove={onDelete} onReplace={setReplacement} />}
        <label className="field">
            <span className="label">Name shown on the website</span>
            <input value={name} onChange={e => setName(e.target.value)} />
        </label>
        <div className="field">
            <span className="label">Category</span>
            <SelectBox className="hub-select-full" label="Category" value={current?.label ?? ''} options={sections.map(s => s.label)}
                onChange={v => setSection(sections.find(s => s.label === v)?.id ?? section)} />
        </div>
        <label className="field">
            <span className="hub-field-hint">Updated date shown on the website</span>
            <input className="subtle" value={date} onChange={e => setDate(e.target.value)} />
        </label>
        <div className="setting-row hub-modal-toggle">
            <div className="text"><span className="title">Show on the website</span><span className="hint">Turn off to hide it without deleting it</span></div>
            <Toggle checked={visible} onChange={setVisible} label="Show on the website" />
        </div>
    </SettingsModal>;
};

export default EditDocumentModal;
